import { useEffect, useState } from 'preact/hooks';
import { describeError, type Session } from '../api/client';
import {
  EXPANDED_MONTHS,
  futureLog,
  type FutureMonth,
} from '../api/future';
import type { WeekStart } from '../api/week';

import { GlyphMark } from '../components/entries/GlyphMark.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { EmptyState } from '../components/feedback/EmptyState.jsx';

/** Six months forward.
 *
 *  Mirrors the canonical frame in `design/handoff/BuJo Mobile PWA.dc.html`: the
 *  two nearest months expand with a month grid and a summary list, the rest
 *  collapse to a count until tapped.
 *
 *  Note the handoff's own `FutureLogScreen.jsx` renders **no** grids — it is the
 *  incomplete one, and the doc is canonical per `design/handoff/readme.md`.
 *  Recorded in `src/components/README.md`.
 *
 *  Marked days are brighter and never coloured only, because the summary list
 *  below is the real content — the grid is orientation, not data.
 */
export function FutureLog({
  session,
  journalDate,
  weekStart,
  onOpenMonth,
  onBack,
}: {
  session: Session;
  journalDate: string;
  weekStart: WeekStart;
  /** A far month is a count until tapped; tapping opens that month's own page,
   *  which is where the frame says the full text lives. */
  onOpenMonth: (month: string) => void;
  onBack: () => void;
}) {
  const [months, setMonths] = useState<FutureMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const found = await futureLog(session, journalDate);
        if (!cancelled) setMonths(found);
      } catch (caught) {
        if (!cancelled) setError(describeError(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, journalDate]);

  const near = months?.slice(0, EXPANDED_MONTHS) ?? [];
  const far = months?.slice(EXPANDED_MONTHS) ?? [];
  const span = months?.length
    ? `${months[0]!.label} – ${months[months.length - 1]!.label}`
    : '';

  return (
    <main style={page}>
      <header style={header}>
        <div style={eyebrow}>BUJO / FUTURE LOG</div>
        <h1 style={h1}>{span || 'FUTURE LOG'}</h1>
      </header>

      {error ? <p style={errorLine}>! {error}</p> : null}

      {near.map((month, index) => (
        <section key={month.month} style={nearBlock}>
          <div style={{ ...monthName, color: index === 0 ? 'var(--bujo-accent)' : 'var(--bujo-text-dim)' }}>
            {month.label}
          </div>
          <MonthGrid month={month} weekStart={weekStart} today={journalDate} />
          {month.items.length === 0 ? (
            <EmptyState line="Nothing planned this month." />
          ) : (
            month.items.map((item, i) => (
              <div key={`${item.at}:${i}`} style={itemRow}>
                <span style={whenCell}>{item.when}</span>
                <GlyphMark state={item.state} size="grid" />
                <span
                  style={{
                    ...itemBody,
                    color:
                      item.state === 'migrated' || item.state === 'cancelled'
                        ? 'var(--bujo-text-dim)'
                        : 'var(--bujo-text-primary)',
                    textDecoration: item.state === 'cancelled' ? 'line-through' : 'none',
                  }}
                >
                  {item.body}
                </span>
              </div>
            ))
          )}
        </section>
      ))}

      {far.map((month) => (
        <Pressable
          key={month.month}
          label={`${month.label}, ${month.items.length || 'no'} item${month.items.length === 1 ? '' : 's'} — open the month`}
          onPress={() => onOpenMonth(month.month)}
        >
          <div style={farRow}>
            <span style={{ ...monthName, color: month.items.length ? 'var(--bujo-text-primary)' : 'var(--bujo-text-faint)' }}>
              {month.label}
            </span>
            <span style={{ ...meta, color: month.items.length ? 'var(--bujo-text-meta)' : 'var(--bujo-text-faint)' }}>
              {month.items.length
                ? `${month.items.length} item${month.items.length === 1 ? '' : 's'}`
                : 'empty'}
            </span>
          </div>
        </Pressable>
      ))}

      {months === null && !error ? <EmptyState line="Reading the months ahead…" /> : null}

      <div style={footnote}>
        The two nearest months expand with day summaries; the rest are a count until
        tapped. Day summaries clip to one line — the month page carries the full text.
      </div>

      <button type="button" onClick={onBack} style={back}>
        ◀ BACK TO INDEX
      </button>
    </main>
  );
}

/** The month, as orientation. Marked days are brighter *and* bolder, so the
 *  marking survives without colour. */
function MonthGrid({
  month,
  weekStart,
  today,
}: {
  month: FutureMonth;
  weekStart: WeekStart;
  today: string;
}) {
  const heads = weekStart === 'sunday' ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const first = month.days[0] ?? '';
  // Which column the 1st falls in, so the grid lines up with real weekdays.
  const weekday = new Date(`${first}T00:00:00Z`).getUTCDay();
  const offset = weekStart === 'sunday' ? weekday : (weekday + 6) % 7;

  return (
    <div style={grid} role="presentation">
      {heads.map((head, i) => (
        <span key={`h${i}`} style={gridHead}>
          {head}
        </span>
      ))}
      {Array.from({ length: offset }, (_, i) => (
        <span key={`pad${i}`} />
      ))}
      {month.days.map((day) => {
        const marked = month.marked.has(day);
        return (
          <span
            key={day}
            style={{
              ...gridDay,
              color: day === today
                ? 'var(--bujo-accent)'
                : marked
                  ? 'var(--bujo-text-primary)'
                  : 'var(--bujo-text-faint)',
              fontWeight: marked ? 700 : 400,
            }}
          >
            {day.slice(8, 10)}
          </span>
        );
      })}
    </div>
  );
}

const page = { padding: 'var(--bujo-gutter)', maxWidth: 720, margin: '0 auto' };
const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const nearBlock = {
  paddingBottom: 'var(--bujo-space-3)',
  marginBottom: 'var(--bujo-space-3)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const monthName = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
};
const grid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '2px 0',
  padding: 'var(--bujo-space-2) 0 var(--bujo-space-3)',
  maxWidth: 260,
};
const gridHead = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-badge)/1.6 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-text-faint)',
  textAlign: 'center' as const,
};
const gridDay = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-mono)',
  textAlign: 'center' as const,
};
const itemRow = {
  display: 'flex',
  gap: 'var(--bujo-space-2)',
  alignItems: 'baseline',
  minHeight: 30,
};
const whenCell = {
  flex: 'none',
  width: 44,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.75 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const itemBody = {
  flex: 1,
  minWidth: 0,
  // One line, as the frame states — the month page carries the whole thing.
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.75 var(--bujo-font-mono)',
};
const farRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--bujo-space-3)',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const meta = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.5 var(--bujo-font-mono)',
};
const footnote = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.7 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  padding: 'var(--bujo-space-4) 0 0',
};
const errorLine = {
  margin: 0,
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/var(--bujo-lh-row) var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
const back = {
  minHeight: 'var(--bujo-tap-min)',
  alignSelf: 'flex-start' as const,
  textAlign: 'left' as const,
  background: 'none',
  border: 0,
  padding: 0,
  marginTop: 'var(--bujo-space-3)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-badge)',
  color: 'var(--bujo-accent)',
  cursor: 'pointer',
};
